<template>
  <div>
    <!-- 卡片视图区 -->
    <el-card class="map-box-card" :class="{ 'box-card-fullscreen': isFullScreen }">
      <div slot="header" class="flexContainer">

        <div class="header-form">
          <div class="cardHeader">门店地图(共{{ total }})</div>
          <div class="searchBar">
            <el-radio-group v-model="select" size="small" class="search-mode-group">
              <el-radio-button label="1">搜地图</el-radio-button>
              <el-radio-button label="2">搜门店</el-radio-button>
            </el-radio-group>
            <el-autocomplete
              v-if="select === '1'"
              size="small"
              v-model="query"
              placeholder="搜索行政区划"
              :fetch-suggestions="queryRegionSuggestions"
              :trigger-on-focus="false"
              :debounce="300"
              @select="handleRegionSelect"
              style="width: 400px;"
            >
              <el-button slot="append" icon="el-icon-search" @click="searchHandler">搜素</el-button>
              <template slot-scope="{ item }">
                <div class="region-suggestion-item">
                  <span class="region-name">{{ item.name }}</span>
                  <span class="region-path">{{ item.fullPath }}</span>
                </div>
              </template>
            </el-autocomplete>
            <el-input
              v-else
              size="small"
              placeholder="请输入关键词"
              v-model="query"
              @keyup.enter.native="searchHandler"
            >
              <el-button slot="append" icon="el-icon-search" @click="searchHandler">搜素</el-button>
            </el-input>
          </div>
          <div>
            <el-button size="small" icon="el-icon-place" plain :type="map.measureMode === 'distance' ? 'warning' : 'primary'"
              :disabled="map.isMeasuring || map.isAddingStore"
              @click="startDistanceMeasure">
              {{ map.isMeasuring ? '测量中...' : '距离测量' }}
            </el-button>
          </div>
          <div>
            <el-button
              type="primary"
              size="small"
              :disabled="map.isMeasuring"
              @click="startAddStore">
              {{ isAddingStore ? '请点击地图' : '添加门店' }}
            </el-button>
          </div>

        </div>
        <div class="cardHeader-actions">
          <el-button type="text" icon="el-icon-refresh" @click="getStoreList">刷新</el-button>
          <el-button type="text" icon="el-icon-full-screen" @click="toggleFullScreen"> {{ isFullScreen ? '退出全屏' : '全屏' }} </el-button>
        </div>
      </div>

      <div class="container">
        <div id="mapdiv"></div>

        <!-- 浮动过滤面板 -->
        <div class="filter-panel" :class="{ 'filter-panel-collapsed': isFilterCollapsed }">
          <div class="filter-header" @click="toggleFilterPanel">
            <span class="filter-title">
              <i class="el-icon-finished"></i>
              筛选
            </span>
            <i :class="isFilterCollapsed ? 'el-icon-arrow-left' : 'el-icon-arrow-down'"></i>
          </div>
          <div class="filter-content" v-show="!isFilterCollapsed">
            <!-- 品牌过滤 -->
            <div class="filter-section">
              <div class="filter-section-title">
                <span>品牌</span>
                <el-button type="text" size="mini" @click="clearAllBrands">清空</el-button>
              </div>
              <el-checkbox-group v-model="selectedBrands" @change="handleBrandChange" class="brand-checkbox-group">
                <el-checkbox v-for="brand in brandOptions" :key="brand" :label="brand" class="brand-checkbox">
                  <span class="brand-dot" :style="{ backgroundColor: getBrandColor(brand) }"></span>
                  {{ brand }}（{{brandCount[brand]}}）
                </el-checkbox>
              </el-checkbox-group>
            </div>

            <!-- 可见性过滤 -->
            <div class="filter-section">
              <div class="filter-section-title">状态</div>
              <el-checkbox-group v-model="selectedVisible" @change="handleVisibleChange" class="visible-checkbox-group">
                <el-checkbox label="正常门店" class="visible-checkbox"></el-checkbox>
                <el-checkbox label="解约门店" class="visible-checkbox"></el-checkbox>
                <el-checkbox label="临时门店" class="visible-checkbox"></el-checkbox>
              </el-checkbox-group>
            </div>

            <!-- 商圈显示控制 -->
            <div class="filter-section">
              <el-checkbox v-model="showCircle" @change="handleCircleChange" class="circle-checkbox">
                显示商圈
              </el-checkbox>
            </div>

            <!-- 区划边界控制 -->
            <div class="filter-section" v-if="currentBoundary">
              <div class="current-boundary-info">
                <i class="el-icon-map-location"></i>
                <span>{{ currentBoundary.name }}</span>
              </div>
              <el-button size="mini" type="danger" plain @click="clearBoundary" style="width: 100%; margin-top: 8px;">
                清除区划边界
              </el-button>
            </div>
          </div>
        </div>

        <!-- 搜索结果浮动面板 -->
        <div
          class="search-result-panel"
          :class="{ 'search-result-panel-collapsed': isSearchResultCollapsed }"
          v-show="hasSearchResults">
          <div class="search-result-header" @click="toggleSearchResultPanel">
            <span class="search-result-title">
              <i class="el-icon-search"></i>
              搜索结果
              <span class="result-count">({{ searchResults.length }})</span>
            </span>
            <i :class="isSearchResultCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-down'"></i>
          </div>
          <div class="search-result-content" v-show="!isSearchResultCollapsed">
            <div class="search-result-list">
              <div
                v-for="(store, index) in searchResults"
                :key="store._id"
                class="search-result-item"
                :class="{ 'result-item-active': activeResultIndex === index }"
                @click="handleSearchResultClick(store, index)">
                <div class="result-brand-dot" :style="{ backgroundColor: getBrandColor(store.brand) }"></div>
                <div class="result-info">
                  <div class="result-name">{{ store.projectName }}</div>
                  <div class="result-address">
                    <i class="el-icon-location-outline"></i>
                    {{ store.address }}
                  </div>
                  <div class="result-meta">
                    <span class="result-brand" :style="{ color: getBrandColor(store.brand) }">{{ store.brand }}</span>
                    <span class="result-range">{{ store.range }}km</span>
                  </div>
                </div>
                <div class="result-action"><i class="el-icon-place"></i></div>
              </div>
            </div>
            <div v-if="searchResults.length === 0" class="search-result-empty">
              <i class="el-icon-info"></i>
              <span>未找到匹配的门店</span>
            </div>
          </div>
        </div>

      </div>

    </el-card>

    <!-- 添加门店对话框 -->
    <el-dialog
      title="添加临时标注点"
      :visible.sync="addDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      @close="cancelAddStore">
      <el-form :model="addStoreForm" :rules="addStoreRules" ref="addStoreFormRef" label-width="100px">
        <el-form-item label="门店名称" prop="projectName">
          <el-input v-model="addStoreForm.projectName" placeholder="请输入门店名称" clearable></el-input>
        </el-form-item>
        <el-form-item label="品牌" prop="brand">
          <el-select v-model="addStoreForm.brand" placeholder="请选择品牌" style="width: 100%" clearable>
            <el-option
              v-for="brand in brandOptions"
              :key="brand"
              :label="brand"
              :value="brand">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="商圈范围" prop="range">
          <el-input-number
            v-model="addStoreForm.range"
            :min="1"
            :max="50"
            :step="1"
            controls-position="right"
            style="width: 100%">
          </el-input-number>
        </el-form-item>
        <el-form-item label="经纬度">
          <el-input :value="`${addStoreForm.lat}, ${addStoreForm.lng}`" disabled></el-input>
        </el-form-item>
        <el-form-item label="地址">
          <el-input v-model="addStoreForm.address" disabled></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="cancelAddStore">取消</el-button>
        <el-button type="primary" @click="confirmAddStore" :loading="isSubmitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 修改商圈对话框 -->
    <el-dialog
      title="修改商圈范围"
      :visible.sync="modifyRangeDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      :destroy-on-close="true">
      <el-form
        :model="modifyRangeForm"
        :rules="modifyRangeRules"
        ref="modifyRangeFormRef"
        label-width="100px"
        size="small">
        <!-- 门店名称 -->
        <el-form-item label="门店名称">
          <el-input v-model="modifyRangeForm.storeName" disabled></el-input>
        </el-form-item>

        <!-- 当前商圈范围 -->
        <el-form-item label="当前商圈范围">
          <el-input :value="modifyRangeForm.currentRange + ' 公里'" disabled>
            <template slot="append">公里（直径）</template>
          </el-input>
        </el-form-item>

        <!-- 新商圈范围 -->
        <el-form-item label="新商圈范围" prop="newRange">
          <el-input-number
            v-model="modifyRangeForm.newRange"
            :min="0.5"
            :step="0.5"
            :precision="1"
            style="width: 100%;"></el-input-number>
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">公里（直径）</div>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="modifyRangeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleModifyRange" :loading="modifyRangeSubmitting">确定</el-button>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import MapTools from '@/utility/mapTools'
import { getAreaName } from '@/utility/staticData'
export default {
  data () {
    return {
      base: '', // 图片url的基础url
      map: {
        measureTool: null,
        measureMode: 'none',
        isMeasuring: false,
        isAddingStore: false
      },
      isFullScreen: false, // 卡片是否全屏

      script: null, // 脚本对象
      scriptLoading: null, // 脚本加载遮罩

      storeList: [], // 所有的门店
      total: 0,

      query: '',
      select: '1',

      brandCount: {
        美滋滋: 0,
        蛮碗香: 0,
        长安大东家: 0,
        虎头张: 0,
        代运营: 0
      },

      // 过滤面板相关
      isFilterCollapsed: false, // 过滤面板是否折叠
      selectedBrands: ['美滋滋'], // 选中的品牌（默认只勾选美滋滋）
      selectedVisible: ['正常门店', '解约门店', '临时门店'], // 选中的可见性
      showCircle: true, // 是否显示商圈
      brandOptions: ['美滋滋', '蛮碗香', '长安大东家', '虎头张', '代运营'], // 品牌选项
      brandColorMap: { // 使用 Map 存储品牌颜色，避免 ESLint 引号问题
        美滋滋: '#FF00CC',
        蛮碗香: '#0099FF',
        虎头张: '#FF6600',
        代运营: '#FFCC00',
        长安大东家: '#9900FF'
      },

      // 添加门店相关数据
      isAddingStore: false, // 是否处于添加模式
      addDialogVisible: false, // 添加门店对话框显示状态
      addStoreForm: { // 添加门店表单数据
        projectName: '',
        brand: '',
        range: 5, // 修改：默认商圈范围从1改为5
        address: '',
        province: '',
        city: '',
        district: '',
        area: '',
        lng: '',
        lat: '',
        flag: 1
      },
      addStoreRules: { // 添加门店表单校验规则
        projectName: [
          { required: true, message: '请输入门店名称', trigger: 'blur' }
        ],
        brand: [
          { required: true, message: '请选择品牌', trigger: 'change' }
        ],
        range: [
          { required: true, message: '请输入商圈范围', trigger: 'blur' },
          { type: 'number', min: 1, max: 50, message: '商圈范围在1-50之间', trigger: 'blur' }
        ]
      },
      isSubmitting: false, // 添加门店提交状态，用于按钮loading

      // 修改商圈相关
      modifyRangeDialogVisible: false, // 修改商圈对话框显示状态
      modifyRangeForm: { // 修改商圈表单
        storeId: '', // 门店ID
        storeName: '', // 门店名称
        currentRange: 0, // 当前商圈范围
        newRange: 0 // 新商圈范围
      },
      modifyRangeRules: { // 表单校验规则
        newRange: [
          { required: true, message: '请输入商圈范围', trigger: 'blur' },
          { type: 'number', min: 0.5, message: '商圈范围最小为0.5公里', trigger: 'change' }
        ]
      },
      modifyRangeSubmitting: false, // 修改商圈提交中状态

      // 搜索结果面板相关
      isSearchResultCollapsed: false, // 搜索结果面板是否折叠
      searchResults: [], // 搜索结果列表
      hasSearchResults: false, // 是否有搜索结果（控制面板显示）
      activeResultIndex: -1, // 当前激活的结果项索引

      // 区划边界相关
      currentBoundary: null // 当前显示的区划信息 { code, name, level }

    }
  },

  watch: {
    select () {
      this.clearSearchResults()
      if (this.select !== '1' && this.currentBoundary) {
        this.clearBoundary()
      }
    },
    query (val) {
      if (val.trim() === '') {
        this.clearSearchResults()
      }
    }
  },

  created () {
    this.base = window.sessionStorage.getItem('base')
    this.loadScript()
    // 绑定全局删除方法
    window.handleDeleteStore = this.handleDeleteStore
    // 绑定全局修改商圈方法
    window.handleModifyRange = this.openModifyRangeDialog
    window.handleCloseStore = this.handleCloseStore
  },
  mounted () {
  },
  beforeDestroy () {
    // 停止添加门店模式
    if (this.map && this.map.stopAddStoreMode) {
      this.map.stopAddStoreMode()
    }

    // 移除MapTools事件监听
    if (this.map) {
      this.map.off('map-click-for-add', this.handleMapClickForAdd)
      this.map.off('error', this.handleMapToolsError)
      this.map.off('temp-marker-click', this.handleTempMarkerClick)
    }

    this.unloadScript()
    // 清理全局方法
    delete window.handleDeleteStore
    delete window.handleModifyRange
    delete window.handleCloseStore
  },
  methods: {

    // 加载地图脚本
    loadScript () {
      this.scriptLoading = this.$loading({
        lock: true,
        text: '正在加载地图数据',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      window.initMap = this.initMap
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${MapTools.apiKey}&libraries=tools,service&callback=initMap`
      document.body.appendChild(script)
      this.script = script
    },

    // 初始化地图
    initMap () {
      this.scriptLoading.close()

      this.map = new MapTools('mapdiv', this.base)

      // 监听MapTools的地图点击事件
      this.map.on('map-click-for-add', this.handleMapClickForAdd)
      this.map.on('error', this.handleMapToolsError)
      // 新增：监听临时标注点点击事件
      this.map.on('temp-marker-click', this.handleTempMarkerClick)

      // 初始化过滤值
      this.initFilters()
      this.getStoreList()
    },

    // 初始化过滤值
    initFilters () {
      if (this.map.brandFilters) {
        this.map.brandFilters = this.selectedBrands
      }
      if (this.map.visibleFilters) {
        this.map.visibleFilters = this.selectedVisible
      }
      // 设置商圈显示状态
      this.map.showCircle = this.showCircle
    },

    // 获取门店列表
    async getStoreList () {
      const { data: res } = await this.$http.get('zhaoshang/storemap/list', { params: { page: 1, pageSize: 5000 } })
      if (res.meta.status !== 200) {
        return this.$message.error(res.meta.msg)
      }
      this.$message.success(`成功加载${res.data.list.length}个门店`)
      this.storeList = res.data.list // 后端已过滤flag>-1，无需前端再过滤
      this.total = res.data.total
      this.brandCount['美滋滋'] = res.data.brandCount['美滋滋'] || 0
      this.brandCount['蛮碗香'] = res.data.brandCount['蛮碗香'] || 0
      this.brandCount['虎头张'] = res.data.brandCount['虎头张'] || 0
      this.brandCount['长安大东家'] = res.data.brandCount['长安大东家'] || 0
      this.brandCount['代运营'] = res.data.brandCount['代运营'] || 0

      this.map.setStoreList(this.storeList)
    },

    // 切换过滤面板折叠状态
    toggleFilterPanel () {
      this.isFilterCollapsed = !this.isFilterCollapsed
    },

    // 品牌过滤变化处理
    handleBrandChange (value) {
      if (this.map && this.map.brandFilters) {
        this.map.brandFilters = value
      }
    },

    // 清空所有品牌勾选
    clearAllBrands () {
      this.selectedBrands = []
      this.handleBrandChange([])
    },

    // 可见性过滤变化处理
    handleVisibleChange (value) {
      if (this.map && this.map.visibleFilters) {
        this.map.visibleFilters = value
      }
    },

    // 商圈显示变化处理
    handleCircleChange (value) {
      if (this.map) {
        this.map.showCircle = value
      }
    },

    // 获取品牌颜色
    getBrandColor (brand) {
      return this.brandColorMap[brand] || '#999'
    },

    // 切换搜索结果面板折叠状态
    toggleSearchResultPanel () {
      this.isSearchResultCollapsed = !this.isSearchResultCollapsed
    },

    // 清空搜索结果
    clearSearchResults () {
      this.searchResults = []
      this.hasSearchResults = false
      this.activeResultIndex = -1
    },

    // 处理搜索结果项点击
    handleSearchResultClick (store, index) {
      this.activeResultIndex = index
      const position = { lat: store.lat, lng: store.lng }
      this.map.moveToPosition(position, 13)
      this.map.showStoreInfo(store)
    },

    // 卸载地图脚本
    unloadScript () {
      // 关闭加载遮罩（如果还在显示）
      if (this.scriptLoading) {
        this.scriptLoading.close()
        this.scriptLoading = null
      }

      // 销毁地图实例和相关资源
      if (this.map && typeof this.map.destroy === 'function') {
        this.map.destroy()
        this.map = null
      }

      // 移除script标签
      if (this.script) {
        document.body.removeChild(this.script)
        this.script = null
      }

      // 清理全局变量
      delete window.TMap
      delete window.initMap
    },

    // 切换卡片全屏
    toggleFullScreen () {
      this.isFullScreen = !this.isFullScreen
      this.$nextTick(() => {
        if (this.map.map && this.map.map.resize) {
          this.map.map.resize()
        }
      })
    },

    // 开始距离测量
    startDistanceMeasure () {
      if (!this.map.measureTool) {
        this.$message.error('测量工具未初始化')
        return
      }

      if (this.map.isMeasuring) {
        this.$message.warning('请先结束当前测量')
        return
      }

      this.map.measureMode = 'distance'
      this.map.isMeasuring = true
      this.map.disableInteraction()

      this.$message.info('在地图上点击添加测量点，双击结束测量')

      this.map.measureTool.measureDistance().then((res) => {
        // 测量结束
        this.map.isMeasuring = false
        this.map.measureMode = 'none'
        this.map.enableInteraction()

        if (res && res.totalDistance) {
          const distance = res.totalDistance >= 1000
            ? (res.totalDistance / 1000).toFixed(2) + ' 公里'
            : res.totalDistance.toFixed(2) + ' 米'
          this.$message.success(`测量结果：${distance}`)
        }
      }).catch((err) => {
        this.map.isMeasuring = false
        this.map.measureMode = 'none'
        this.map.enableInteraction()
        console.error('距离测量错误:', err)
        this.$message.error('距离测量失败')
      })
    },

    // 点击搜索
    async searchHandler () {
      this.clearSearchResults()
      if (this.select === '1') {
        await this.searchMap()
      } else if (this.select === '2') {
        await this.searchMarker()
      }
    },

    // 搜索标注点
    async searchMarker () {
      if (this.query.trim() === '') {
        this.$message.error('请输入关键字')
        return
      }
      try {
        const { data: res } = await this.$http.get('zhaoshang/storemap/search', { params: { keyword: this.query } })
        if (res.meta.status !== 200) {
          return this.$message.error(res.meta.msg)
        }

        // 存储搜索结果并显示面板
        this.searchResults = res.data
        this.hasSearchResults = res.data.length > 0
        this.activeResultIndex = -1
        this.isSearchResultCollapsed = false

        if (res.data.length > 0) {
          this.$message.success(`找到${res.data.length}个匹配的门店`)
          // 如果只有一个结果，直接定位
          if (res.data.length === 1) {
            this.handleSearchResultClick(res.data[0], 0)
          }
        } else {
          this.$message.warning('未找到匹配的门店')
        }
      } catch (error) {
        this.$message.error('搜索门店失败')
        console.error(error)
      }
    },

    // 搜索地图（新增功能）
    async searchMap () {
      if (this.query.trim() === '') {
        this.$message.error('请输入地址关键字')
        return
      }

      try {
        // 调用后台地图API代理接口
        const { data: res } = await this.$http.get('map/geocoder', {
          params: { address: this.query.trim() }
        })

        if (res.meta.status !== 200) {
          return this.$message.error(res.meta.msg || '地址解析失败')
        }

        const result = res.data
        if (!result.location || !result.location.lat || !result.location.lng) {
          return this.$message.error('未找到该地址或坐标信息不完整')
        }

        // 解析坐标 - 从location对象中获取
        const position = {
          lat: parseFloat(result.location.lat),
          lng: parseFloat(result.location.lng)
        }

        // 设置交互模式为搜索模式
        const success = this.map.setInteractionMode('search')
        if (!success) {
          return
        }

        // 创建临时标注点
        this.map.createTempMarker(position, result.title || this.query.trim())

        // 移动地图到该位置并缩放到街道级（精确位置需要足够大的比例尺）
        this.map.moveToPosition(position, 17.4)

        // 显示成功信息
        const addressInfo = result.address_components
          ? `${result.address_components.province || ''}${result.address_components.city || ''}${result.address_components.district || ''}`
          : ''
        this.$message.success(`已定位到: ${result.title || addressInfo || '目标位置'}`)
      } catch (error) {
        this.$message.error('地址搜索失败')
        console.error(error)
      }
    },

    // 查询行政区划建议
    async queryRegionSuggestions (queryString, cb) {
      if (!queryString || queryString.trim() === '') {
        const empty = []
        return cb(empty)
      }
      try {
        const { data: res } = await this.$http.get('region/search', {
          params: { keyword: queryString.trim(), limit: 10 }
        })
        if (res.meta.status === 200 && res.data.length > 0) {
          const suggestions = res.data.map(item => ({
            ...item,
            value: item.name
          }))
          cb(suggestions)
        } else {
          const empty = []
          return cb(empty)
        }
      } catch (error) {
        console.error('搜索行政区划失败:', error)
        const empty = []
        return cb(empty)
      }
    },

    // 处理区划建议选中
    handleRegionSelect (item) {
      this.currentBoundary = {
        code: item.code,
        name: item.name,
        level: item.level
      }
      this.map.drawDistrictBoundary(item.code, item.level)
    },

    // 清除区划边界
    clearBoundary () {
      this.map.clearDistrictBoundary()
      this.currentBoundary = null
      this.query = ''
    },

    // 开始添加门店
    startAddStore () {
      if (!this.map || !this.map.startAddStoreMode) {
        this.$message.error('地图尚未初始化')
        return
      }
      const success = this.map.startAddStoreMode()
      if (success) {
        this.isAddingStore = true
        this.$message.info('请在地图上点击选择门店位置')
      }
    },

    // 处理临时标注点点击事件
    handleTempMarkerClick (data) {
      const { position } = data
      // 填充表单数据
      this.addStoreForm.lat = position.lat
      this.addStoreForm.lng = position.lng

      // 获取地址信息
      this.getAddressFromCoords(position.lat, position.lng)
    },

    // 处理地图点击事件（用于添加门店）
    handleMapClickForAdd (data) {
      const { lat, lng } = data
      this.addStoreForm.lat = lat
      this.addStoreForm.lng = lng

      this.getAddressFromCoords(lat, lng)
    },

    // 处理MapTools错误
    handleMapToolsError (message) {
      this.$message.error(message)
    },

    // 通过经纬度获取地址信息
    async getAddressFromCoords (lat, lng) {
      const location = `${lat},${lng}`
      try {
        const { data: res } = await this.$http.get('map/reverseGeocoder', { params: { location } })

        if (res.meta.status !== 200) {
          this.$message.error('获取地址信息失败，请重新选择位置')
          if (this.map && this.map.stopAddStoreMode) {
            this.map.stopAddStoreMode()
          }
          this.isAddingStore = false
          return
        }

        const { ad_info: adInfo, formatted_addresses: formattedAddresses } = res.data
        this.addStoreForm.province = adInfo.province || ''
        this.addStoreForm.city = adInfo.city || ''
        this.addStoreForm.district = adInfo.district || ''
        this.addStoreForm.address = formattedAddresses.recommend || ''
        this.addStoreForm.area = getAreaName(adInfo.province) || '其他'
        this.showAddDialog()
      } catch (error) {
        this.$message.error('获取地址信息失败，请重新选择位置')

        if (this.map && this.map.stopAddStoreMode) {
          this.map.stopAddStoreMode()
        }
        this.isAddingStore = false
      }
    },

    // 显示添加门店对话框
    showAddDialog () {
      this.addDialogVisible = true
    },

    // 取消添加门店
    cancelAddStore () {
      this.addDialogVisible = false
      this.resetAddForm()

      // 清除临时标注点（如果是搜索模式创建的）
      if (this.map && this.map.interactionMode === 'search') {
        this.map.clearTempMarker()
        // 恢复浏览模式
        this.map.setInteractionMode('browse')
      }

      // 停止添加门店模式
      if (this.map && this.map.stopAddStoreMode) {
        this.map.stopAddStoreMode()
      }
      this.isAddingStore = false
    },

    // 重置添加表单
    resetAddForm () {
      this.addStoreForm = {
        projectName: '',
        brand: '',
        range: 5, // 修改：默认商圈范围从1改为5
        address: '',
        province: '',
        city: '',
        district: '',
        area: '',
        lng: '',
        lat: '',
        flag: 1
      }
      this.$nextTick(() => {
        if (this.$refs.addStoreFormRef) {
          this.$refs.addStoreFormRef.clearValidate()
        }
      })
    },

    // 确认添加门店
    async confirmAddStore () {
      if (!this.$refs.addStoreFormRef) {
        return
      }
      this.$refs.addStoreFormRef.validate(async (valid) => {
        if (!valid) {
          return false
        }
        this.isSubmitting = true
        try {
          const { data: res } = await this.$http.post('zhaoshang/storemap/add', this.addStoreForm)
          if (res.meta.status !== 200) {
            this.$message.error(res.meta.msg || '添加门店失败')
            return
          }
          this.map.addStore(res.data)
          this.$message.success('添加门店成功')

          // 成功添加后清除临时标注点并恢复浏览模式
          if (this.map && this.map.interactionMode === 'search') {
            this.map.clearTempMarker()
            this.map.setInteractionMode('browse')
          }

          this.cancelAddStore()
        } catch (error) {
          this.$message.error(`添加门店失败：${error.message || '网络错误'}`)
        } finally {
          this.isSubmitting = false
        }
      })
    },

    // 删除门店
    async handleDeleteStore (storeId, storeName) {
      try {
        // 二次确认
        await this.$confirm(`确认删除临时门店 "${storeName}"？删除后无法恢复。`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        // 关闭信息窗口
        if (this.map) {
          this.map.closeInfoWindow()
        }

        const loading = this.$loading({
          lock: true,
          text: '正在删除...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })

        const { data: res } = await this.$http.delete(`zhaoshang/storemap/remove/${storeId}`)

        loading.close()

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg || '删除门店失败')
          return
        }

        this.map.removeStore(storeId)
        this.$message.success('删除门店成功')
      } catch (error) {
        // 用户取消删除
        if (error === 'cancel') {
          return
        }
        console.error('删除门店失败:', error)
        this.$message.error('删除门店失败: ' + (error.message || '网络错误'))
      }
    },

    // 关闭门店
    async handleCloseStore (storeId, storeName) {
      try {
        // 二次确认
        await this.$confirm(`确认将 "${storeName}" 永久关闭吗？关闭后彻底释放商圈不再显示。`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        // 关闭信息窗口
        if (this.map) {
          this.map.closeInfoWindow()
        }

        const loading = this.$loading({
          lock: true,
          text: '正在处理...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })

        const { data: res } = await this.$http.put(`zhaoshang/storemap/close/${storeId}`)

        loading.close()

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg || '关闭门店失败')
          return
        }

        this.map.removeStore(storeId)
        this.$message.success('关闭门店成功')
      } catch (error) {
        // 用户取消删除
        if (error === 'cancel') {
          return
        }
        console.error('关闭门店失败:', error)
        this.$message.error('关闭门店失败: ' + (error.message || '网络错误'))
      }
    },

    // 打开修改商圈对话框
    openModifyRangeDialog (storeId) {
      // 查找门店数据
      const store = this.map.findStoreById(storeId)
      if (!store) {
        this.$message.error('门店数据不存在')
        return
      }

      // 初始化表单数据
      this.modifyRangeForm = {
        storeId: storeId,
        storeName: store.projectName || '未知门店',
        currentRange: store.range || 0,
        newRange: store.range || 0
      }

      // 重置表单校验
      if (this.$refs.modifyRangeFormRef) {
        this.$refs.modifyRangeFormRef.clearValidate()
      }

      // 打开对话框
      this.modifyRangeDialogVisible = true
    },

    // 处理修改商圈
    async handleModifyRange () {
      // 表单校验
      try {
        await this.$refs.modifyRangeFormRef.validate()
      } catch (error) {
        this.$message.error('请完善表单信息')
        return
      }

      // 关闭信息窗口
      if (this.map) {
        this.map.closeInfoWindow()
      }

      try {
        this.modifyRangeSubmitting = true

        // 调用修改商圈接口
        const { data: res } = await this.$http.put(`zhaoshang/storemap/modifyRange/${this.modifyRangeForm.storeId}`, {
          range: this.modifyRangeForm.newRange
        })

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg || '修改商圈范围失败')
          return
        }

        this.$message.success('修改商圈范围成功')

        // 更新本地门店数据
        this.map.updateStoreRange(this.modifyRangeForm.storeId, this.modifyRangeForm.newRange)

        // 关闭对话框
        this.modifyRangeDialogVisible = false
      } catch (error) {
        this.$message.error('修改商圈范围失败: ' + (error.message || '网络错误'))
      } finally {
        this.modifyRangeSubmitting = false
      }
    }
  }

}
</script>

<style lang="less" scoped>

.flexContainer {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-form{
    display:flex;
    align-items: center;
    gap:10px;

    .cardHeader {
      font-size: 20px;
      font-weight: bold;
    }

    .searchBar{
      margin-left:50px;
      display: flex;
      align-items: center;
      gap: 10px;

      .el-input{
        width: 400px;
      }
    }

    .el-input{
      width: 300px;
    }
  }
}

.cardHeader-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.box-card-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  border-radius: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
}

.box-card-fullscreen .container {
  height: calc(100vh - 80px);
}

.container {
  display: flex;
  height: 750px;
  width: 100%;
  gap: 10px;
  position: relative;
}
#mapdiv {
  width: 100%;
  height: 100%;
}

/* 浮动过滤面板样式 */
.filter-panel {
  position: absolute;
  top: 20px;
  left: 20px;
  width: 200px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #E4E7ED;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
  pointer-events: auto;
}

.filter-panel-collapsed {
  width: 80px;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #E4E7ED;
  cursor: pointer;
  background: linear-gradient(135deg, #F5F7FA 0%, #FFFFFF 100%);
  border-radius: 8px 8px 0 0;
  pointer-events: auto;
}

.filter-header:hover {
  background: linear-gradient(135deg, #ECF5FF 0%, #FFFFFF 100%);
}

.filter-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-content {
  padding: 15px;
  pointer-events: auto;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-section:last-child {
  margin-bottom: 0;
}

.filter-section-title {
  font-size: 12px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #F0F2F5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brand-checkbox-group,
.visible-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.brand-checkbox,
.visible-checkbox {
  margin: 0 !important;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #606266;
}

.brand-checkbox:hover,
.visible-checkbox:hover {
  color: #409EFF;
}

.brand-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}

/* 全屏时调整面板位置 */
.box-card-fullscreen .filter-panel {
  top: 20px;
  left: 20px;
}

/* 搜索结果浮动面板样式 */
.search-result-panel {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 300px;
  max-height: calc(100% - 40px);
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #E4E7ED;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
}

.search-result-panel-collapsed {
  width: 50px;
}

.search-result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #E4E7ED;
  cursor: pointer;
  background: linear-gradient(135deg, #F5F7FA 0%, #FFFFFF 100%);
  border-radius: 8px 8px 0 0;
  flex-shrink: 0;
}

.search-result-header:hover {
  background: linear-gradient(135deg, #ECF5FF 0%, #FFFFFF 100%);
}

.search-result-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 6px;
}

.result-count {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.search-result-content {
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.search-result-list {
  max-height: 500px;
  overflow-y: auto;
  padding: 10px;
}

.search-result-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  margin-bottom: 8px;
  background: #FAFAFA;
  border: 1px solid #EBEEF5;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 10px;
}

.search-result-item:hover {
  background: #F0F9FF;
  border-color: #409EFF;
  transform: translateX(-2px);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.search-result-item.result-item-active {
  background: #ECF5FF;
  border-color: #409EFF;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

.result-brand-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}

.result-info {
  flex: 1;
  min-width: 0;
}

.result-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-address {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-address i {
  flex-shrink: 0;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.result-range {
  color: #909399;
  background: #F5F7FA;
  padding: 2px 6px;
  border-radius: 3px;
}

.result-action {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409EFF;
  opacity: 0;
  transition: opacity 0.2s;
}

.search-result-item:hover .result-action {
  opacity: 1;
}

.result-action i {
  font-size: 18px;
}

.search-result-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #909399;
  font-size: 14px;
}

.search-result-empty i {
  font-size: 32px;
  margin-bottom: 10px;
  opacity: 0.5;
}

/* 全屏时调整搜索结果面板位置 */
.box-card-fullscreen .search-result-panel {
  top: 20px;
  right: 20px;
}

/* 区划搜索建议项样式 */
.region-suggestion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;

  .region-name {
    font-size: 14px;
    color: #303133;
    font-weight: 500;
  }

  .region-path {
    font-size: 12px;
    color: #909399;
    margin-left: 10px;
    flex-shrink: 0;
  }
}

/* 当前区划边界信息 */
.current-boundary-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #409EFF;
  font-weight: 500;
}
</style>

<!-- 全局样式，用于地图弹窗 -->
<style lang="less">

.map-box-card{
  .el-card__body{
    padding:0px;
  }
}
.store-info-popup h3 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #303133;
}

.store-info-popup p {
  margin: 5px 0;
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
}

.store-info-popup strong {
  color: #303133;
}

.store-info-actions {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}

.store-delete-btn {
  padding: 5px 15px;
  font-size: 12px;
  border: none;
  border-radius: 3px;
  background-color: #f56c6c;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;
}

.store-delete-btn:hover {
  background-color: #f78989;
}

.store-delete-btn:active {
  background-color: #dd6161;
}

.store-modify-range-btn {
  padding: 5px 15px;
  font-size: 12px;
  border: none;
  border-radius: 3px;
  background-color: #409eff;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-left: 10px;
}

.store-modify-range-btn:hover {
  background-color: #66b1ff;
}

.store-modify-range-btn:active {
  background-color: #3a8ee6;
}
</style>
